import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, ListGroup, Modal, Spinner, Table } from "react-bootstrap";
import { mcpUserTokenService } from "../../services/mcpUserToken.service";

const EXPIRY_CHOICES = [30, 90, 180, 365];
const DEFAULT_EXPIRY = 90;
const OVERLAP_CHOICES = [
  { hours: 0, label: "End the old token now" },
  { hours: 24, label: "Keep the old token working for 24 hours" },
  { hours: 72, label: "Keep the old token working for 72 hours" },
  { hours: 168, label: "Keep the old token working for 7 days" },
];
const MAX_DESCRIPTION = 200;
const MIN_SEARCH = 2;
const SEARCH_DELAY_MS = 300;

const when = (value) => (value ? new Date(value).toLocaleString() : "Never");

/** The reason a call failed, in the server's own words when it gave one (a limit, a bad value), otherwise a plain default. */
export function messageFrom(error, fallback) {
  const data = error?.response?.data;
  return typeof data === "string" && data.length > 0 ? data : fallback;
}

export function statusOf(token) {
  if (!token.isActive) return { label: "Revoked", variant: "secondary" };
  if (token.isExpired) return { label: "Expired", variant: "warning" };
  return { label: "Active", variant: "success" };
}

const personLabel = (t) => t.userName || t.userEmail || t.userId;

/**
 * Personal MCP tokens (#2719) for the Product Team. scope "all" is the admin view: everyone's tokens, issue one to any active
 * user, rotate, revoke. scope "mine" is the same list narrowed to the signed-in person, for their own Profile page.
 *
 * A token is shown exactly once, in the dialog that creates or rotates it. It is held in component state only while that
 * dialog is open and never written to the store, local storage or the page address.
 */
function McpUserTokens({ user, scope = "all", hideTitle = false }) {
  const allowed = !!user?.isProductTeam && !user?.isImpersonating;
  const mine = scope === "mine";
  const ownId = user?.userId;

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  // One dialog does both jobs: { mode: "create" } or { mode: "rotate", token }.
  const [dialog, setDialog] = useState(null);
  const [description, setDescription] = useState("");
  const [days, setDays] = useState(DEFAULT_EXPIRY);
  const [overlap, setOverlap] = useState(0);
  const [person, setPerson] = useState(null);
  const [search, setSearch] = useState("");
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await mcpUserTokenService.list(mine ? ownId : undefined);
      setTokens(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      setError(messageFrom(e, "Could not load the MCP tokens."));
    } finally {
      setLoading(false);
    }
  }, [mine, ownId]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  // The person picker: wait for a pause in typing, then look people up.
  useEffect(() => {
    if (!dialog || dialog.mode !== "create" || mine || person) return undefined;
    const text = search.trim();
    if (text.length < MIN_SEARCH) {
      setFound([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await mcpUserTokenService.findUsers(text);
        setFound(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        setDialogError(messageFrom(e, "Could not look people up."));
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, dialog, mine, person]);

  const shown = useMemo(() => {
    const text = filter.trim().toLowerCase();
    if (!text) return tokens;
    return tokens.filter((t) => [t.userName, t.userEmail, t.description].some((v) => (v || "").toLowerCase().includes(text)));
  }, [tokens, filter]);

  if (!allowed) return null;

  const resetDialogFields = () => {
    setDescription("");
    setDays(DEFAULT_EXPIRY);
    setOverlap(0);
    setPerson(null);
    setSearch("");
    setFound([]);
    setDialogError("");
    setResult(null);
    setCopied(false);
  };

  const openCreate = () => {
    resetDialogFields();
    setDialog({ mode: "create" });
  };

  const openRotate = (token) => {
    resetDialogFields();
    setDialog({ mode: "rotate", token });
  };

  const closeDialog = () => {
    // Forget the token the moment the dialog goes away: it cannot be shown again.
    resetDialogFields();
    setDialog(null);
  };

  const submit = async () => {
    setBusy(true);
    setDialogError("");
    try {
      const response =
        dialog.mode === "rotate"
          ? await mcpUserTokenService.rotate(dialog.token.tokenId, { graceHours: Number(overlap), expiresInDays: Number(days) })
          : await mcpUserTokenService.create({
              userId: mine ? undefined : person?.userId,
              description: description.trim() || null,
              expiresInDays: Number(days),
            });
      setResult(response.data);
      await load();
    } catch (e) {
      setDialogError(messageFrom(e, dialog.mode === "rotate" ? "Could not rotate the token." : "Could not create the token."));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const revoke = async (token) => {
    const name = token.description || `token ${token.tokenId}`;
    const who = mine ? "" : ` for ${personLabel(token)}`;
    if (!window.confirm(`Revoke "${name}"${who}? Any AI client using it stops working straight away. This cannot be undone.`)) return;
    try {
      await mcpUserTokenService.revoke(token.tokenId);
      await load();
    } catch (e) {
      setError(messageFrom(e, "Could not revoke the token."));
    }
  };

  const canSubmitCreate = mine || !!person;
  const rotating = dialog?.mode === "rotate";
  const title = result ? (rotating ? "Your replacement MCP token" : "The new MCP token") : rotating ? "Rotate an MCP token" : "Create an MCP token";
  const recipient = result ? result.userName || result.userEmail || (mine ? "you" : result.userId) : "";

  return (
    <div className="mcp-user-tokens mt-4" data-testid="mcp-user-tokens">
      <div className="d-flex justify-content-between align-items-center mb-2">
        {hideTitle ? <span /> : <h5 className="mb-0">{mine ? "My MCP tokens" : "User MCP tokens"}</h5>}
        <Button size="sm" variant="primary" onClick={openCreate}>
          {mine ? "Create token" : "Create token for a user"}
        </Button>
      </div>
      <p className="text-muted small">
        {mine
          ? "A token lets your AI client (Claude, Cursor, Copilot Studio) use the Runbook Builder MCP server as you. Treat it like a password: it is shown once, when you create it."
          : "A token lets someone's AI client use the Runbook Builder MCP server as that person. You decide who gets one. Each token is shown once, when it is created or rotated; send it to them through a secure channel. Rotating issues a replacement and ends the old one."}
      </p>

      {!mine && (
        <Form.Control
          size="sm"
          className="mb-2"
          placeholder="Filter by person or description"
          aria-label="Filter tokens"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <Spinner animation="border" size="sm" />}
      {!loading && tokens.length === 0 && !error && <p className="text-muted">There are no MCP tokens yet.</p>}

      {shown.length > 0 && (
        <Table size="sm" responsive>
          <thead>
            <tr>
              {!mine && <th>Person</th>}
              <th>Description</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Expires</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((t) => {
              const status = statusOf(t);
              return (
                <tr key={t.tokenId}>
                  {!mine && (
                    <td>
                      {personLabel(t)}
                      {t.userName && t.userEmail && <div className="small text-muted">{t.userEmail}</div>}
                    </td>
                  )}
                  <td>{t.description || <span className="text-muted">(no description)</span>}</td>
                  <td>{when(t.createdOn)}</td>
                  <td>{t.lastUsedTimestamp ? when(t.lastUsedTimestamp) : "Never used"}</td>
                  <td>{when(t.expiresOn)}</td>
                  <td>
                    <Badge bg={status.variant}>{status.label}</Badge>
                  </td>
                  <td className="text-end text-nowrap">
                    {t.isActive && (
                      <>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openRotate(t)}>
                          Rotate
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => revoke(t)}>
                          Revoke
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal show={!!dialog} onHide={closeDialog} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {result ? (
            <>
              <Alert variant="warning">Copy this token now. It is not stored anywhere readable, so it cannot be shown again.</Alert>
              <p className="mb-2">
                Token for <strong>{recipient}</strong>
              </p>
              <Form.Control readOnly value={result.token} aria-label="The new MCP token" onFocus={(e) => e.target.select()} autoComplete="off" />
              <div className="mt-2 d-flex align-items-center">
                <Button size="sm" variant="outline-primary" onClick={copy}>
                  Copy token
                </Button>
                {copied && <span className="ms-2 text-success small">Copied</span>}
              </div>
              <p className="small text-muted mt-3 mb-0">
                It goes in the <code>CLOUDSYNC-API-KEY</code> header and expires {when(result.expiresOn)}.
                {result.replacedTokenId != null &&
                  (result.replacedTokenEndsOn
                    ? ` The old token keeps working until ${when(result.replacedTokenEndsOn)}.`
                    : " The old token has stopped working.")}
              </p>
            </>
          ) : (
            <>
              {dialogError && <Alert variant="danger">{dialogError}</Alert>}
              {rotating ? (
                <p>
                  Replace <strong>{dialog.token.description || `token ${dialog.token.tokenId}`}</strong>
                  {!mine && <> for <strong>{personLabel(dialog.token)}</strong></>} with a new token.
                </p>
              ) : (
                <>
                  {!mine && (
                    <Form.Group className="mb-3">
                      <Form.Label>Who is it for?</Form.Label>
                      {person ? (
                        <div>
                          <Badge bg="primary" className="me-2">
                            {person.name || person.email}
                          </Badge>
                          <span className="small text-muted">{person.email}</span>
                          <Button size="sm" variant="link" onClick={() => setPerson(null)}>
                            Change
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Form.Control placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search people" autoComplete="off" />
                          {found.length > 0 && (
                            <ListGroup className="mt-1">
                              {found.map((p) => (
                                <ListGroup.Item action key={p.userId} onClick={() => setPerson(p)}>
                                  {p.name || p.email} <span className="small text-muted">{p.email}</span>
                                  {p.isProductTeam && <Badge bg="success" className="ms-2">Product Team</Badge>}
                                  {!p.isProductTeam && p.isSuperUser && <Badge bg="primary" className="ms-2">Super User</Badge>}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          )}
                        </>
                      )}
                    </Form.Group>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label>What is it for?</Form.Label>
                    <Form.Control
                      value={description}
                      maxLength={MAX_DESCRIPTION}
                      placeholder="For example: Claude Desktop on their laptop"
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}
              {rotating && (
                <Form.Group className="mb-3">
                  <Form.Label>The old token</Form.Label>
                  <Form.Select value={overlap} onChange={(e) => setOverlap(e.target.value)} aria-label="The old token">
                    {OVERLAP_CHOICES.map((o) => (
                      <option key={o.hours} value={o.hours}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <Form.Group>
                <Form.Label>{rotating ? "The new token expires after" : "Expires after"}</Form.Label>
                <Form.Select value={days} onChange={(e) => setDays(e.target.value)} aria-label="Expires after">
                  {EXPIRY_CHOICES.map((d) => (
                    <option key={d} value={d}>
                      {d} days
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {result ? (
            <Button variant="primary" onClick={closeDialog}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={closeDialog} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={submit} disabled={busy || (!rotating && !canSubmitCreate)}>
                {busy ? "Working..." : rotating ? "Rotate" : "Create"}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default McpUserTokens;
